import type { OpenIdConfiguration } from 'angular-auth-oidc-client';

import type { AppSettingsDto } from '../app-settings/models/app-settings.model';
import type {
    PlatformConfigurationSource,
    PublicAppConfiguration,
} from './platform-sts-configuration.loader';
import { environment } from '../../../environments/environment';

type ConfigurationValue = string | number | boolean;

interface ConfigurationLogRow {
    setting: string;
    source: string;
    value: ConfigurationValue;
}

const LOCAL_ENVIRONMENT_SOURCE =
    'LOCAL | Angular environment replacement compiled into the web bundle';
const PUBLIC_API_SOURCE = 'API | .NET Web API /settings/public (anonymous public appsettings)';
const AUTHENTICATED_API_SOURCE =
    'API | .NET Web API /settings (authenticated tenant/user appsettings)';
const PUBLIC_API_NOT_APPLIED_SOURCE = 'NOT APPLIED | .NET Web API /settings/public was not applied';

/**
 * Logs only the small set of local build settings useful for diagnosing which
 * environment was compiled into the browser bundle. The final effective
 * snapshot below remains the authoritative runtime output.
 */
export function logCompiledEnvironmentConfiguration(): void {
    const configuredEnvironment = environment as unknown as Record<string, unknown>;

    logTable(
        '1. Local Angular environment candidates',
        'These are local build-time values. They are not retrieved from the API.',
        [
            row(
                'environment.buildConfiguration',
                getSetting(configuredEnvironment, 'buildConfiguration')
            ),
            row(
                'environment.environmentFile',
                getSetting(configuredEnvironment, 'environmentFile')
            ),
            row('environment.env', getSetting(configuredEnvironment, 'env')),
            row('environment.production', getSetting(configuredEnvironment, 'production')),
            row('environment.apiRoot', getSetting(configuredEnvironment, 'apiRoot')),
            row(
                'environment.apAutomationApiRoot',
                getSetting(configuredEnvironment, 'apAutomationApiRoot')
            ),
            row('environment.apiRootPython', getSetting(configuredEnvironment, 'apiRootPython')),
            row('environment.JDE_API_URL', getSetting(configuredEnvironment, 'JDE_API_URL')),
            row('environment.clientRoot', getSetting(configuredEnvironment, 'clientRoot')),
            row('environment.stsAuthority', getSetting(configuredEnvironment, 'stsAuthority')),
            row('environment.clientId', getSetting(configuredEnvironment, 'clientId')),
            row('environment.scope', getSetting(configuredEnvironment, 'scope')),
            row(
                'environment.platformConfiguration.enabled',
                getSetting(configuredEnvironment, 'platformConfiguration.enabled')
            ),
            row(
                'environment.platformConfiguration.required',
                getSetting(configuredEnvironment, 'platformConfiguration.required')
            ),
        ],
        LOCAL_ENVIRONMENT_SOURCE
    );
}

export function logPublicConfigurationRequest(endpoint: string): void {
    console.info(
        `[Configuration][API] Requesting public platform configuration. source="${PUBLIC_API_SOURCE}" endpoint="${endpoint}"`
    );
}

export function logPublicConfigurationFallback(endpoint: string, failureCode: string): void {
    console.warn(
        `[Configuration][API] Public platform configuration request failed. Local STS values are used only when fallback is permitted; otherwise startup stops. source="${LOCAL_ENVIRONMENT_SOURCE}" endpoint="${endpoint}" reason="${failureCode}"`
    );
}

/**
 * Logs one consolidated runtime snapshot after startup configuration has been
 * resolved. This is the authoritative diagnostic output: the values below
 * are the values the running Angular application is using at this point.
 *
 * Deliberately omitted from every diagnostic path:
 * - OIDC behavior values such as response type, silent renew, refresh-token
 *   usage, and token-renewal timing.
 * - Public-configuration status, environment, and revision metadata.
 * - Authenticated tenant/user app-setting values.
 */
export function logFinalEffectiveConfiguration(
    source: PlatformConfigurationSource,
    oidcConfiguration: OpenIdConfiguration | null,
    publicConfiguration: PublicAppConfiguration | null,
    appSettings: AppSettingsDto | null
): void {
    const authenticationSource =
        source === 'central' ? PUBLIC_API_SOURCE : LOCAL_ENVIRONMENT_SOURCE;
    const version = (key: 'web' | 'webApi' | 'fastApi'): unknown =>
        appSettings?.appVersion?.[key] ?? publicConfiguration?.appVersion[key];
    const versionSource = (key: 'web' | 'webApi' | 'fastApi'): string =>
        appSettings?.appVersion?.[key] !== undefined
            ? AUTHENTICATED_API_SOURCE
            : publicConfiguration?.appVersion[key] !== undefined
            ? PUBLIC_API_SOURCE
            : PUBLIC_API_NOT_APPLIED_SOURCE;

    console.group('[Configuration] FINAL EFFECTIVE SETTINGS — the app is running on these values');
    console.info(
        'Authoritative runtime snapshot. The source column identifies local Angular values versus values retrieved from the .NET API. Only intentionally approved diagnostic settings are shown.'
    );
    console.table([
        row(
            'runtime.buildConfiguration',
            getSetting(environment, 'buildConfiguration'),
            LOCAL_ENVIRONMENT_SOURCE
        ),
        row(
            'runtime.environmentFile',
            getSetting(environment, 'environmentFile'),
            LOCAL_ENVIRONMENT_SOURCE
        ),
        row('runtime.environment', getSetting(environment, 'env'), LOCAL_ENVIRONMENT_SOURCE),
        row('runtime.production', getSetting(environment, 'production'), LOCAL_ENVIRONMENT_SOURCE),
        row('runtime.apiRoot', getSetting(environment, 'apiRoot'), LOCAL_ENVIRONMENT_SOURCE),
        row(
            'runtime.apAutomationApiRoot',
            getSetting(environment, 'apAutomationApiRoot'),
            LOCAL_ENVIRONMENT_SOURCE
        ),
        row(
            'runtime.apiRootPython',
            getSetting(environment, 'apiRootPython'),
            LOCAL_ENVIRONMENT_SOURCE
        ),
        row(
            'runtime.JDE_API_URL',
            getSetting(environment, 'JDE_API_URL'),
            LOCAL_ENVIRONMENT_SOURCE
        ),
        row('runtime.clientRoot', getSetting(environment, 'clientRoot'), LOCAL_ENVIRONMENT_SOURCE),
        row('runtime.auth.authority', oidcConfiguration?.authority, authenticationSource),
        row('runtime.auth.clientId', oidcConfiguration?.clientId, authenticationSource),
        row('runtime.auth.scope', oidcConfiguration?.scope, authenticationSource),
        row('runtime.auth.redirectUrl', oidcConfiguration?.redirectUrl, LOCAL_ENVIRONMENT_SOURCE),
        row(
            'runtime.auth.postLogoutRedirectUri',
            oidcConfiguration?.postLogoutRedirectUri,
            LOCAL_ENVIRONMENT_SOURCE
        ),
        row('runtime.appVersion.web', version('web'), versionSource('web')),
        row('runtime.appVersion.webApi', version('webApi'), versionSource('webApi')),
        row('runtime.appVersion.fastApi', version('fastApi'), versionSource('fastApi')),
    ]);
    console.groupEnd();
}

function logTable(
    title: string,
    description: string,
    rows: ConfigurationLogRow[],
    groupSource: string
): void {
    console.groupCollapsed(`[Configuration] ${title}`);
    console.info(`${description} groupSource="${groupSource}"`);
    console.table(rows);
    console.groupEnd();
}

function row(
    setting: string,
    value: unknown,
    source: string = LOCAL_ENVIRONMENT_SOURCE
): ConfigurationLogRow {
    return {
        setting,
        source,
        value: formatValue(value),
    };
}

function getSetting(root: unknown, path: string): unknown {
    return path.split('.').reduce<unknown>((current, segment) => {
        if (!current || typeof current !== 'object') {
            return undefined;
        }

        return (current as Record<string, unknown>)[segment];
    }, root);
}

function formatValue(value: unknown): ConfigurationValue {
    if (value === undefined) {
        return '[not defined in this build]';
    }

    if (value === null) {
        return '[null]';
    }

    if (typeof value === 'string') {
        return value.length > 0 ? value : '[empty]';
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return value;
    }

    try {
        return JSON.stringify(value) ?? '[unavailable]';
    } catch {
        return '[unavailable]';
    }
}
